import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Building2, User, Mail, Phone, MapPin, Calendar, FileText, Download, Send, ChevronDown, CheckCircle, Store } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Dropdown from '@/components/ui/dropdown';
import { DealerSurveyData } from '@/api/types';
import { useTranslation } from 'react-i18next';

interface FormSummaryProps {
  formData: DealerSurveyData;
  onSubmit: () => void;
  isSubmitting: boolean;
  readOnly?: boolean;
}

const FormSummary: React.FC<FormSummaryProps> = ({ formData, onSubmit, isSubmitting, readOnly = false }) => {
  const { t, i18n } = useTranslation('common');

  // Utility funkce pro převod kódu země na název
  const getCountryName = (countryCode: string): string => {
    const isEnglish = i18n.language === 'en';
    const countryNames: Record<string, { cs: string; en: string }> = {
      'CZ': { cs: 'Česká republika', en: 'Czech Republic' },
      'SK': { cs: 'Slovensko', en: 'Slovakia' },
      'PL': { cs: 'Polsko', en: 'Poland' },
      'DE': { cs: 'Německo', en: 'Germany' },
      'AT': { cs: 'Rakousko', en: 'Austria' },
      'HU': { cs: 'Maďarsko', en: 'Hungary' },
      'US': { cs: 'Spojené státy', en: 'United States' },
      'GB': { cs: 'Velká Británie', en: 'United Kingdom' },
      'FR': { cs: 'Francie', en: 'France' },
      'IT': { cs: 'Itálie', en: 'Italy' },
      'ES': { cs: 'Španělsko', en: 'Spain' },
      // Přidáme více podle potřeby
    };
    
    const country = countryNames[countryCode];
    if (country) {
      return isEnglish ? country.en : country.cs;
    }
    
    // Fallback na kód země pokud není v mapě
    return countryCode;
  };

  const getFileName = (extension: string) => {
    return `dotaznik_${formData.companyName || 'partner'}_${new Date().toISOString().split('T')[0]}.${extension}`;
  };

  const handleExportJSON = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', getFileName('json'));
    linkElement.click();
  };

  const handleExportCSV = () => {
    // Připravíme data pro CSV export
    const csvData = [
      ['Pole', 'Hodnota'],
      ['Název firmy', formData.companyName || ''],
      ['Kontaktní osoba', formData.contactName || ''],
      ['Telefon', formData.contactPhone || ''],
      ['E-mail', formData.contactEmail || ''],
      ['Nemá showroom', formData.hasNoShowroom ? 'Ano' : 'Ne'],
      ['Ulice', formData.showroomStreet || ''],
      ['Město', formData.showroomCity || ''],
      ['PSČ', formData.showroomZipCode || ''],
      ['Země', formData.showroomCountry ? getCountryName(formData.showroomCountry) : ''],
      ['Datum návštěvy', formData.visitDate || ''],
      ['Obchodní zástupce', formData.salesRep || ''],
      ['Co zaujalo', formData.whatAttracted || ''],
      ['Co chybělo', formData.whatMissing || ''],
      ['Konkurence', Array.isArray(formData.competitors) ? formData.competitors.join(', ') : (formData.competitors || '')],
      ['Cenové vnímání', getValueLabel(formData.pricePerception || '', pricePerceptionOptions)],
      ['Regionální potenciál', getValueLabel(formData.regionPotential || '', regionPotentialOptions)],
      ['Zájem o showroom', getValueLabel(formData.showroomInterest || '', showroomInterestOptions)],
      ['Preferovaná barva do showroomu', getValueLabel(formData.showroomColorPreference || '', showroomColorPreferenceOptions)],
      ['Marketingové materiály', getValueLabel(formData.marketingMaterials || '', marketingMaterialsOptions)],
      ['Očekávaná podpora', (formData.expectedSupport || []).map(s => supportOptions[s as keyof typeof supportOptions] || s).join('; ')],
      ['Jiná podpora', formData.otherSupport || ''],
      ['Způsob prodeje', getValueLabel(formData.salesMethod || [], salesMethodOptions)],
      ['Maximální dodací lhůta', getValueLabel(formData.maxDeliveryTime || '', maxDeliveryTimeOptions)],
      ['Typický zákazník', formData.typicalCustomer || ''],
      ['Celkem kamen za rok', formData.totalStovesPerYear || ''],
      ['Designová kamna za rok', formData.designStovesPerYear || ''],
      ['První dojem z designu', getValueLabel(formData.designImpression || '', designImpressionOptions)],
      ['Zájem o spolupráci', getValueLabel(formData.cooperationInterest || '', cooperationInterestOptions)],
      ['Reakce na značku', getValueLabel(formData.brandReaction || '', brandReactionOptions)],
      ['Má zájem o', (formData.interests || []).map(i => interestsOptions[i as keyof typeof interestsOptions] || i).join('; ')],
      ['Bylo dohodnuto', (formData.nextSteps || []).map(s => nextStepsOptions[s as keyof typeof nextStepsOptions] || s).join('; ')],
      ['Jiný dohodnutý krok', formData.otherStep || ''],
      ['Komentář obchodníka', formData.comments || '']
    ];

    // Konverze do CSV formátu
    const csvContent = csvData.map(row => 
      row.map(field => `"${field.toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    // Přidáme BOM pro správné zobrazení českých znaků
    const BOM = '\uFEFF';
    const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(BOM + csvContent);
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', getFileName('csv'));
    linkElement.click();
  };

  const handleSendEmail = () => {
    const emailBody = `Dotazník pro návštěvu prodejce kamen - ${formData.companyName}\n\nDatum návštěvy: ${formData.visitDate}\nObchodní zástupce: ${formData.salesRep}\n\nVíce informací v příloze.`;
    const mailtoLink = `mailto:?subject=Dotazník - ${formData.companyName}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
  };

  const getValueLabel = (value: string | string[], options?: { [key: string]: string }) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Neuvedeno';
    }
    if (options && value) {
      return options[value] || value;
    }
    return value || 'Neuvedeno';
  };

  const exportItems = [
    {
      label: 'JSON',
      onClick: handleExportJSON,
      icon: <Download size={14} />
    },
    {
      label: 'CSV',
      onClick: handleExportCSV,
      icon: <Download size={14} />
    },
    {
      label: 'E-mail',
      onClick: handleSendEmail,
      icon: <Mail size={14} />
    }
  ];

  // Helper function to format arrays
  const formatArray = (arr: string[] | undefined, emptyText: string = t('form_summary.not_specified')) => {
    if (!arr || arr.length === 0) return emptyText;
    return arr.join(', ');
  };

  // Helper function to format single value
  const formatValue = (value: string | undefined, emptyText: string = t('form_summary.not_specified')) => {
    if (!value || typeof value !== 'string' || value.trim() === '') return emptyText;
    return value;
  };

  // Define all option mappings with translations
  const nextStepsOptions = {
    'send-materials': t('form_step_3.step_send_materials'),
    'price-offer': t('form_step_3.step_price_offer'),
    'send-sample': t('form_step_3.step_send_sample'),
    'next-visit': t('form_step_3.step_next_visit')
  };

  const supportOptions = {
    'marketing': t('form_step_4.support_marketing'),
    'training': t('form_step_4.support_training'),
    'service': t('form_step_4.support_service'),
    'none': t('form_step_4.support_none'),
    'materials': t('form_step_3.interest_materials'),
    'samples': 'Vzorky produktů',
    'exclusive-territory': 'Exkluzivní území',
    'price-support': 'Cenová podpora',
    'technical-support': 'Technická podpora'
  };

  const pricePerceptionOptions = {
    'fair-value': t('form_step_2.price_fair_value'),
    'higher-expected': t('form_step_2.price_higher_expected'),
    'surprisingly-good': t('form_step_2.price_surprisingly_good')
  };

  const regionPotentialOptions = {
    'yes': t('form_step_2.region_yes'),
    'maybe': t('form_step_2.region_maybe'),
    'no': t('form_step_2.region_no')
  };

  const showroomInterestOptions = {
    'buy-display': t('form_step_2.showroom_buy_display'),
    'buy-connect': t('form_step_2.showroom_buy_connect'),
    'shop-in-shop': t('form_step_2.showroom_shop_in_shop'),
    'maybe': t('form_step_2.showroom_maybe'),
    'no': t('form_step_2.showroom_no')
  };

  const marketingMaterialsOptions = {
    'excellent': t('form_step_2.marketing_excellent'),
    'interesting': t('form_step_2.marketing_interesting'),
    'standard': t('form_step_2.marketing_standard'),
    'not-interested': t('form_step_2.marketing_not_interested')
  };

  const showroomColorPreferenceOptions = {
    'antracit': t('form_step_2.color_antracit'),
    'silver-grey': t('form_step_2.color_silver_grey'),
    'pearl-beige': t('form_step_2.color_pearl_beige'),
    'mauve': t('form_step_2.color_mauve'),
    'pearl-brown': t('form_step_2.color_pearl_brown'),
    'red-brown': t('form_step_2.color_red_brown')
  };

  const salesMethodOptions = {
    'with-installation': t('form_step_4.sales_with_installation'),
    'without-installation': t('form_step_4.sales_without_installation'),
    'eshop': t('form_step_4.sales_eshop')
  };

  const maxDeliveryTimeOptions = {
    'in-stock': t('form_step_4.delivery_in_stock'),
    'one-month': t('form_step_4.delivery_one_month'),
    'two-months': t('form_step_4.delivery_two_months'),
    'three-months': t('form_step_4.delivery_three_months')
  };

  const cooperationInterestOptions = {
    'yes': t('form_step_3.cooperation_yes'),
    'maybe': t('form_step_3.cooperation_maybe'),
    'no': t('form_step_3.cooperation_no')
  };

  const designImpressionOptions = {
    'very-positive': t('form_step_3.design_very_positive'),
    'rather-positive': t('form_step_3.design_rather_positive'),
    'neutral': t('form_step_3.design_neutral'),
    'rather-negative': t('form_step_3.design_rather_negative'),
    'negative': t('form_step_3.design_negative')
  };

  const brandReactionOptions = {
    'knows-positive': t('form_step_3.brand_knows_positive'),
    'knows-negative': t('form_step_3.brand_knows_negative'),
    'unknown-interested': t('form_step_3.brand_unknown_interested'),
    'unknown-notrust': t('form_step_3.brand_unknown_notrust')
  };

  const interestsOptions = {
    'training': t('form_step_3.interest_training'),
    'presentation': t('form_step_3.interest_presentation'),
    'exclusive-region': t('form_step_3.interest_exclusive'),
    'marketing-materials': t('form_step_3.interest_materials')
  };

  // const cooperationOptions: Record<string, string> = {
  //   'yes': t('form_step_3.cooperation_yes'),
  //   'maybe': t('form_step_3.cooperation_maybe'),
  //   'no': t('form_step_3.cooperation_no')
  // };

  // const interestOptions: Record<string, string> = {
  //   'training': t('form_step_3.interest_training'),
  //   'presentation': t('form_step_3.interest_presentation'),
  //   'exclusivity': t('form_step_3.interest_exclusive')
  // };

  // Helper function to translate arrays
  const translateArray = (arr: string[] | undefined, optionsMap: Record<string, string>) => {
    if (!arr || arr.length === 0) return t('form_summary.not_specified');
    return arr.map(item => optionsMap[item] || item).join(', ');
  };

  // Helper function to translate single value
  const translateValue = (value: string | undefined, optionsMap: Record<string, string>) => {
    if (!value || value.trim() === '') return t('form_summary.not_specified');
    return optionsMap[value] || value;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          {t('form_summary.title')}
        </h2>
        <p className="text-slate-600">
          Zkontrolujte všechny údaje před odesláním
        </p>
      </div>

      {/* Identifikace prodejce */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader className="bg-orange-100">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <Building2 className="h-5 w-5" />
            {t('form_summary.section_identification')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.company_name')}:</span>
              </div>
              <p className="text-slate-700 ml-6">{formatValue(formData.companyName)}</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.contact_name')}:</span>
              </div>
              <p className="text-slate-700 ml-6">{formatValue(formData.contactName)}</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.contact_phone')}:</span>
              </div>
              <p className="text-slate-700 ml-6">{formatValue(formData.contactPhone)}</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.contact_email')}:</span>
              </div>
              <p className="text-slate-700 ml-6">{formatValue(formData.contactEmail)}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              <span className="font-medium">{t('form_step_1.showroom_address')}:</span>
              {formData.hasNoShowroom && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                  <Store className="h-3 w-3" />
                  Nemá showroom
                </span>
              )}
            </div>
            <p className="text-slate-700 ml-6">
              {formatValue(formData.showroomStreet)}, {formatValue(formData.showroomCity)}, {formatValue(formData.showroomZipCode)}, {getCountryName(formData.showroomCountry)}
              {formData.hasNoShowroom && <span className="text-slate-500 italic"> (adresa sídla)</span>}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.visit_date')}:</span>
              </div>
              <p className="text-slate-700 ml-6">
                {formData.visitDate ? new Date(formData.visitDate).toLocaleDateString('cs-CZ') : t('form_summary.not_specified')}
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{t('form_step_1.sales_rep')}:</span>
              </div>
              <p className="text-slate-700 ml-6">{formatValue(formData.salesRep)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hodnocení produktu a ceny */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="bg-green-100">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <FileText className="h-5 w-5" />
            {t('form_summary.section_product')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.what_attracted')}:</span>
              <p className="text-slate-700">{formatValue(formData.whatAttracted)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.what_missing')}:</span>
              <p className="text-slate-700">{formatValue(formData.whatMissing)}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_2.competitors')}:</span>
            <p className="text-slate-700">{formatArray(formData.competitors)}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.price_perception_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.pricePerception, pricePerceptionOptions)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.region_potential_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.regionPotential, regionPotentialOptions)}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.showroom_interest_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.showroomInterest, showroomInterestOptions)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.showroom_color_preference_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.showroomColorPreference, showroomColorPreferenceOptions)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_2.marketing_materials_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.marketingMaterials, marketingMaterialsOptions)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Obchodní podmínky */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader className="bg-purple-100">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <FileText className="h-5 w-5" />
            {t('form_summary.section_conditions')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_4.expected_support_title')}:</span>
            <p className="text-slate-700">{translateArray(formData.expectedSupport, supportOptions)}</p>
          </div>
          
          {formData.otherSupport && (
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_4.support_other')}:</span>
              <p className="text-slate-700">{formatValue(formData.otherSupport)}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_4.sales_method_title')}:</span>
              <p className="text-slate-700">{translateArray(formData.salesMethod, salesMethodOptions)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_4.delivery_time_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.maxDeliveryTime, maxDeliveryTimeOptions)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Poznámky obchodníka */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader className="bg-orange-100">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <FileText className="h-5 w-5" />
            {t('form_summary.section_notes')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_3.design_impression_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.designImpression, designImpressionOptions)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_3.cooperation_interest_title')}:</span>
              <p className="text-slate-700">{translateValue(formData.cooperationInterest, cooperationInterestOptions)}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_3.brand_reaction_title')}:</span>
            <p className="text-slate-700">{translateValue(formData.brandReaction, brandReactionOptions)}</p>
          </div>
          
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_3.interests_title')}:</span>
            <p className="text-slate-700">{translateArray(formData.interests, interestsOptions)}</p>
          </div>
          
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_3.next_steps_title')}:</span>
            <p className="text-slate-700">{translateArray(formData.nextSteps, nextStepsOptions)}</p>
          </div>
          
          {formData.otherStep && (
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_3.other_step')}:</span>
              <p className="text-slate-700">{formatValue(formData.otherStep)}</p>
            </div>
          )}
          
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_3.comments')}:</span>
            <p className="text-slate-700 whitespace-pre-wrap">
              {formatValue(formData.comments, t('form_summary.no_comment'))}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Prodejní potenciál */}
      <Card className="border-teal-200 bg-teal-50">
        <CardHeader className="bg-teal-100">
          <CardTitle className="flex items-center gap-2 text-teal-800">
            <FileText className="h-5 w-5" />
            {t('form_summary.section_potential')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <span className="font-medium">{t('form_step_4.typical_customer')}:</span>
            <p className="text-slate-700">{formatValue(formData.typicalCustomer)}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_4.total_stoves_per_year')}:</span>
              <p className="text-slate-700">{formatValue(formData.totalStovesPerYear)}</p>
            </div>
            
            <div className="space-y-2">
              <span className="font-medium">{t('form_step_4.design_stoves_per_year')}:</span>
              <p className="text-slate-700">{formatValue(formData.designStovesPerYear)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="space-y-4">
        <Alert className="border-orange-200 bg-orange-50">
          <CheckCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>{t('form_summary.ready_message')}</strong>
          </AlertDescription>
        </Alert>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4">
          {/* Export dropdown vlevo */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">{t('form_summary.export_label')}</span>
            <Dropdown
              trigger={
                <Button 
                  variant="outline"
                  size="sm"
                  className="border-slate-300 hover:bg-slate-50 flex items-center gap-2"
                >
                  <Download size={14} />
                  {t('form_summary.select_format')}
                  <ChevronDown size={14} />
                </Button>
              }
              items={exportItems}
            />
          </div>

          {/* Tlačítko vpravo - pouze v editačním módu */}
          {!readOnly && (
            <div className="flex justify-end">
              <Button 
                onClick={onSubmit}
                disabled={isSubmitting}
                className="text-white flex items-center gap-2 px-6"
                style={{ backgroundColor: '#DA7D3B', borderColor: '#DA7D3B' }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#C5702F';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = '#DA7D3B';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Odesílání...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t('form_summary.submit_raynet')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormSummary; 